/* eslint-disable max-lines-per-function */
import { authenticatedFetch } from "@lumi/data";
import { useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";

interface PresignedUrlResponse {
  uploadUrl: string;
  key: string; // R2 key for later retrieval
  imageAssetId: string; // ImageAsset ID for client reference
  imageType: string;
  expiresAt: string;
}

interface ImageValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

interface UploadCompleteResponse {
  success: boolean;
  metadata: {
    width?: number;
    height?: number;
    format?: string;
    hasAlpha?: boolean;
    colorSpace?: string;
    aspectRatio?: string;
  };
  validation: ImageValidationResult;
}

/**
 * Extract dimensions from an image file
 * Returns null if extraction fails (non-blocking)
 */
async function extractImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null); // Don't fail upload if dimension extraction fails
    };

    img.src = url;
  });
}

interface UploadImageParams {
  submissionId?: string; // Optional for backward compatibility
  titleId?: string; // For admin title editing
  file: File;
  imageType: "poster" | "backdrop" | "logo" | "horizontal-tile" | "vertical-tile";
  mode?: "submission" | "title"; // Which endpoint to use
}

interface UploadImageResult {
  key: string; // R2 key to store in database
  submissionId?: string;
  titleId?: string;
  imageType: string;
  validation?: ImageValidationResult; // Validation results from upload-complete
  metadata?: {
    width?: number;
    height?: number;
    format?: string;
    aspectRatio?: string;
  };
}

interface UseImageUploadResult {
  uploadImage: (params: UploadImageParams) => Promise<UploadImageResult>;
  uploading: boolean;
  error: string | null;
}

/**
 * Hook for uploading images to R2 (lumi-uploads bucket)
 * Returns the R2 key for later retrieval via presigned read URLs
 */
export function useImageUpload(): UseImageUploadResult {
  const { accessToken, refreshAuth } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadImage = async ({
    submissionId,
    titleId,
    file,
    imageType,
    mode = "submission",
  }: UploadImageParams): Promise<UploadImageResult> => {
    if (!accessToken) {
      throw new Error("Not authenticated");
    }

    const entityId = titleId || submissionId;
    if (!entityId) {
      throw new Error("Either submissionId or titleId is required");
    }

    setUploading(true);
    setError(null);

    try {
      // Step 1: Extract image dimensions (non-blocking - continues even if this fails)
      const dimensions = await extractImageDimensions(file);

      // Step 2: Get presigned URL from API (use admin endpoint for titles)
      const endpoint = mode === "title" ? "/v1/admin/images/presigned-url" : "/v1/provider/images/presigned-url";

      const presignedResponse = await authenticatedFetch<PresignedUrlResponse>(
        `${import.meta.env.VITE_API_BASE_URL}${endpoint}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...(titleId ? { titleId } : { submissionId }),
            imageType,
            fileName: file.name,
            fileSize: file.size,
            contentType: file.type,
            // Include dimensions if successfully extracted
            ...(dimensions && { width: dimensions.width, height: dimensions.height }),
          }),
        },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      // Step 3: Upload the file to the presigned URL
      const uploadResponse = await fetch(presignedResponse.uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": file.type,
        },
        body: file,
      });

      console.log("useImageUpload: R2 upload response status:", uploadResponse.status);

      // R2 presigned uploads may return 401 even on success (known Cloudflare behavior)
      // Accept 2xx, 204, and 401 as success
      if (!uploadResponse.ok && uploadResponse.status !== 401) {
        const errorText = await uploadResponse.text();
        console.error("useImageUpload: R2 upload failed:", errorText);
        throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }

      console.log("useImageUpload: Upload successful, returning R2 key");

      // Step 4: Call upload-complete endpoint to analyze and validate the image
      let validation: ImageValidationResult | undefined;
      let enrichedMetadata: UploadCompleteResponse["metadata"] | undefined;

      try {
        const completeEndpoint =
          mode === "title" ? "/v1/admin/images/upload-complete" : "/v1/provider/images/upload-complete";

        const completeResponse = await authenticatedFetch<UploadCompleteResponse>(
          `${import.meta.env.VITE_API_BASE_URL}${completeEndpoint}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              imageAssetId: presignedResponse.imageAssetId,
            }),
          },
          accessToken,
          createRefreshCallback(refreshAuth),
        );

        validation = completeResponse.validation;
        enrichedMetadata = completeResponse.metadata;

        // Log validation results for debugging
        if (validation.errors.length > 0) {
          console.warn("Image validation errors:", validation.errors);
        }
        if (validation.warnings.length > 0) {
          console.info("Image validation warnings:", validation.warnings);
        }
      } catch (err) {
        // Don't fail the upload if metadata enrichment fails
        console.error("Failed to enrich image metadata:", err);
      }

      // Step 5: Return the R2 key and metadata
      return {
        key: presignedResponse.key,
        ...(titleId ? { titleId } : { submissionId }),
        imageType,
        validation,
        metadata: enrichedMetadata,
      };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Upload failed";
      setError(errorMessage);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  return {
    uploadImage,
    uploading,
    error,
  };
}
